// Authentication API endpoints

import { apiClient } from './client';
import type { LoginRequest, LoginResponse, SignupRequest, SignupResponse, RefreshTokenResponse } from '../types/auth';

const API_URL = process.env.API_URL || 'http://localhost:8080';

/**
 * Authentication API functions
 */
export const authApi = {
  /**
   * Login with email and password
   *
   * @param email - User email address
   * @param password - User password
   * @param accountId - Optional account UUID to scope JWT to
   * @returns Login response with JWT token and account info
   */
  login: (email: string, password: string, accountId?: string): Promise<LoginResponse> => {
    const request: LoginRequest = {
      email,
      password,
      account_id: accountId,
    };
    return apiClient.post<LoginResponse>('/api/auth/login', request);
  },

  /**
   * Sign up with email and password
   *
   * Creates a new user with a default account and returns JWT token
   *
   * @param email - User email address
   * @param password - User password (minimum 8 characters)
   * @returns Signup response with JWT token and default account
   */
  signup: (email: string, password: string): Promise<SignupResponse> => {
    const request: SignupRequest = {
      email,
      password,
    };
    return apiClient.post<SignupResponse>('/api/auth/signup', request);
  },

  /**
   * Refresh an access token using a refresh token.
   * Uses direct fetch (not apiClient) to avoid recursion with the 401 interceptor.
   */
  refresh: async (refreshToken: string): Promise<RefreshTokenResponse> => {
    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Refresh failed');
    }

    return response.json();
  },

  /**
   * Logout by revoking the refresh token family.
   * Uses direct fetch (not apiClient) since this is called during logout.
   * Best-effort — failures are silently ignored.
   */
  logout: async (refreshToken: string): Promise<void> => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      // Best-effort — don't block logout on network errors
    }
  },
};
